import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../components/Layout';
import BankScopeField from '../../components/BankScopeField';
import ScopeField from '../../components/ScopeField';
import {
  createBorletteCoverageRule, deleteBorletteCoverageRule,
  listBorletteCoverageRules, updateBorletteCoverageRule,
} from '../../api/borletteCoverage';
import './KonbinezonRatioPage.css';

// RÈG 3 — Pousantaj sou Borlet. Quand ON, le nombre de numéros Borlette DISTINCTS
// vendus dans la journée sur la portée (toutes loteries confondues) est plafonné à
// available_pct % des 100 numéros. Remplace CoverageLimit pour Borlette.
// Portée company (Tout) / group / bank — le plus spécifique gagne côté vente.

const PCT_OPTIONS = [50, 60, 70, 80, 90, 100];
const DEFAULTS = { enabled: false, available_pct: 70 };

const scopeParams = (filter, scopeId) => {
  if (filter === 'Groupe') return { group: scopeId };
  if (filter === 'Banque') return { bank: scopeId };
  return {};
};

const BorletteCoveragePage = () => {
  const [filter, setFilter] = useState('Tout');
  const [scopeId, setScopeId] = useState('');
  const [scopeName, setScopeName] = useState('');

  const [ruleId, setRuleId] = useState(null);
  const [enabled, setEnabled] = useState(DEFAULTS.enabled);
  const [pct, setPct] = useState(DEFAULTS.available_pct);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  const scopeReady = filter === 'Tout' || !!scopeId;

  const load = useCallback(() => {
    if (!scopeReady) return;
    setLoading(true);
    setError('');
    setSaved(false);
    listBorletteCoverageRules(scopeParams(filter, scopeId))
      .then((rows) => {
        const rule = Array.isArray(rows) ? rows[0] : null;
        if (rule) {
          setRuleId(rule.id);
          setEnabled(rule.enabled);
          setPct(Number(rule.available_pct));
        } else {
          setRuleId(null);
          setEnabled(DEFAULTS.enabled);
          setPct(DEFAULTS.available_pct);
        }
      })
      .catch(() => setError('Impossible de charger la règle.'))
      .finally(() => setLoading(false));
  }, [filter, scopeId, scopeReady]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSaved(false);
    const payload = { enabled, available_pct: pct };
    try {
      if (ruleId) {
        await updateBorletteCoverageRule(ruleId, payload);
      } else {
        const created = await createBorletteCoverageRule({ ...payload, ...scopeParams(filter, scopeId) });
        setRuleId(created.id);
      }
      setSaved(true);
    } catch (err) {
      setError('Sauvegarde impossible. Vérifiez les valeurs.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!ruleId) return;
    setSaving(true);
    setError('');
    try {
      await deleteBorletteCoverageRule(ruleId);
      setRuleId(null);
      setEnabled(DEFAULTS.enabled);
      setPct(DEFAULTS.available_pct);
      setSaved(false);
    } catch (err) {
      setError('Suppression impossible.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      <p className="kr-breadcrumb">
        Ruta : <Link to="/dashboard" className="bc-link">Menu</Link>
        {' >> '}
        <Link to="/securite" className="bc-link">Securite</Link>
        {' >> '}
        <span className="bc-current">Pousantaj sou Borlet</span>
      </p>

      <div className="kr-card">
        <div className="kr-card-header">
          <span className="kr-card-title">Pousantaj sou Borlet (RÈG 3)</span>
        </div>
        <div className="kr-card-body">
          <div className="kr-radio-row">
            {['Tout', 'Groupe', 'Banque'].map((opt) => (
              <label key={opt} className="kr-radio-label">
                <input
                  type="radio"
                  name="bc-filter"
                  value={opt}
                  checked={filter === opt}
                  onChange={() => { setFilter(opt); setScopeId(''); setScopeName(''); }}
                />
                {opt}
              </label>
            ))}
          </div>

          {filter !== 'Tout' && (
            <div className="kr-row">
              <label className="kr-label">{filter}</label>
              {filter === 'Banque' ? (
                <BankScopeField
                  id={scopeId}
                  name={scopeName}
                  onResolve={(id, name) => { setScopeId(id); setScopeName(name); }}
                  inputClass="kr-input kr-input-sm"
                  iconClass="kr-icon-btn"
                  nameClass="kr-scope-name"
                  errClass="kr-lookup-err"
                />
              ) : (
                <ScopeField
                  filter={filter}
                  id={scopeId}
                  name={scopeName}
                  onResolve={(id, nm) => { setScopeId(id); setScopeName(nm); }}
                  inputClass="kr-input kr-input-sm"
                  iconClass="kr-icon-btn"
                  nameClass="kr-scope-name"
                  errClass="kr-lookup-err"
                />
              )}
            </div>
          )}

        </div>
      </div>

      <div className="kr-card">
        <div className="kr-card-header">
          <span className="kr-card-title">
            Règle {filter === 'Tout' ? 'générale (company)' : filter.toLowerCase()}
          </span>
        </div>

        {!scopeReady ? (
          <div className="kr-card-body">
            <p className="kr-hint">Choisir un {filter.toLowerCase()} ci-dessus.</p>
          </div>
        ) : loading ? (
          <div className="kr-card-body"><p>Chargement...</p></div>
        ) : (
          <form className="kr-card-body" onSubmit={handleSave}>
            <label className="kr-toggle-row">
              <input
                type="checkbox"
                checked={enabled}
                onChange={(e) => { setEnabled(e.target.checked); setSaved(false); }}
              />
              <span>Aktive règ la</span>
              {!ruleId && <span className="kr-hint">(pas encore de règle pour cette portée)</span>}
            </label>

            <div className="kr-row">
              <label className="kr-label">Pousantaj</label>
              <select
                className="kr-input"
                value={pct}
                disabled={!enabled}
                onChange={(e) => { setPct(Number(e.target.value)); setSaved(false); }}
              >
                {PCT_OPTIONS.map((p) => <option key={p} value={p}>{p} %</option>)}
              </select>
              <span className="kr-hint">{pct} boul sou 100 disponib pou jwe</span>
            </div>

            <div className="kr-btn-row">
              <button type="submit" className="kr-save-btn" disabled={saving}>
                {ruleId ? 'Anrejistre' : 'Kreye règ la'}
              </button>
              {ruleId && (
                <button type="button" className="kr-del-btn" onClick={handleDelete} disabled={saving}>
                  Efase
                </button>
              )}
            </div>

            {saved && <p className="kr-ok">Konfigirasyon anrejistre.</p>}
            {error && <p className="kr-error">{error}</p>}
          </form>
        )}
      </div>

      <div className="kr-card">
        <div className="kr-card-header">
          <span className="kr-card-title">Kijan sa mache</span>
        </div>
        <div className="kr-card-body">
          <p className="kr-explain">
            Gen <strong>100 boul</strong> nan sistèm nan (00–99). Pousantaj la fikse
            konbyen boul <strong>diferan</strong> ki ka jwe nan yon jounen :
          </p>
          <p className="kr-explain kr-explain-ex">
            {pct} % → <strong>{pct} boul</strong> sou 100. Depi {pct} boul diferan
            deja jwe jodi a (sou tout loteri yo ansanm, sou pòte a), yon
            <strong> nouvo</strong> boul ap bloke. Yon boul ki deja jwe rete
            disponib.
          </p>
          <p className="kr-explain kr-explain-note">
            RÈG 3 ranplase « Limite d'Exposition » pou Borlette (lòt pwodwi yo rete
            sou Limite d'Exposition). Pòte pi presi genyen : bank {'>'} group {'>'} company.
          </p>
        </div>
      </div>
    </Layout>
  );
};

export default BorletteCoveragePage;
